import React, {FC, useEffect, useState} from 'react'
import {Col, Form, Button, Row} from 'react-bootstrap'
import Alertize from 'app/shared/Alfacom/Alert/Alertize'
import Loading from 'app/shared/Alfacom/Loading/Loading'
import {useFormik} from 'formik'
import {GetFormikValidClass} from 'app/shared/Common/Util'
import FormikErrors from '../../../shared/FormikErrors/FormikErrors'
import * as yup from 'yup'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import axios from 'axios'
import {updatePassword} from '../core/_requests'
import {AuthModel} from '../core/_models'
import {getAuth, setAuth, removeAuth} from '../core/AuthHelpers'
import clsx from 'clsx'
import Iconize from 'app/shared/Alfacom/Icon/Iconize'
import {VALIDATION, VALIDATION_ERROR} from 'app/shared/Common/Constants'
import YupPassword from 'yup-password'
YupPassword(yup);

export interface ChangePasswordProps {
  show: boolean
  isModal?: boolean
  showSpinner?: boolean
  subMessage?: string
  showDefaultSuccessMessage?: boolean
  showCancel?: boolean
  onCancel?: () => void
  onSuccessSave?: () => void
  keepCurrentAuthAlive?: boolean
  onFailedDetectCurrentAuth?: () => void
}

const initialValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirmation: '',
}

const validationSchema = yup.object().shape({
  currentPassword: yup.string().required(VALIDATION_ERROR.PASSWORD.REQUIREDx('Current Password')),
  newPassword: yup
    .string()
    .min(VALIDATION.PASSWORD.FORM.MIN, VALIDATION_ERROR.PASSWORD.MIN(VALIDATION.PASSWORD.FORM.MIN))
    .max(VALIDATION.PASSWORD.FORM.MAX, VALIDATION_ERROR.PASSWORD.MAX(VALIDATION.PASSWORD.FORM.MAX))
    .minLowercase(VALIDATION.PASSWORD.FORM.MIN_LOWERCASE, VALIDATION_ERROR.PASSWORD.MIN_LOWERCASE(VALIDATION.PASSWORD.FORM.MIN_LOWERCASE))
    .minUppercase(VALIDATION.PASSWORD.FORM.MIN_UPPERCASE, VALIDATION_ERROR.PASSWORD.MIN_UPPERCASE(VALIDATION.PASSWORD.FORM.MIN_UPPERCASE))
    .minNumbers(VALIDATION.PASSWORD.FORM.MIN_NUMBERS, VALIDATION_ERROR.PASSWORD.MIN_NUMBERS(VALIDATION.PASSWORD.FORM.MIN_NUMBERS))
    .minSymbols(VALIDATION.PASSWORD.FORM.MIN_SYMBOLS, VALIDATION_ERROR.PASSWORD.MIN_SYMBOLS(VALIDATION.PASSWORD.FORM.MIN_SYMBOLS))
    .required(VALIDATION_ERROR.PASSWORD.REQUIREDx('Password')),
  newPasswordConfirmation: yup
    .string()
    .required(VALIDATION_ERROR.PASSWORD.REQUIREDx('Password confirmation'))
    .when('newPassword', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: yup.string().oneOf([yup.ref('newPassword')], 'Passwords do not match'),
    }),
})

const ChangePassword: FC<ChangePasswordProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [tempAuth, setTempAuth] = useState<AuthModel | undefined>(undefined)

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        setLoading(true)
        setSuccess(false)
        setError(undefined)

        if (!props.keepCurrentAuthAlive && tempAuth) setAuth(tempAuth)
        await updatePassword(values.currentPassword, values.newPassword, values.newPasswordConfirmation)
        if (!props.keepCurrentAuthAlive && tempAuth) removeAuth()

        setLoading(false)
        setSuccess(true)
        resetState()
        props.onSuccessSave?.()
      } catch (error) {
        console.error(error)
        setLoading(false)
        setSubmitting(false)

        if (axios.isAxiosError(error) && error.response) {
          setError(error.response.data.Message)
        } else {
          setError(`Unexpected error while updating password.`)
        }
      }
    },
  })

  const resetState = () => {
    setError(undefined)
    formik.resetForm()
  }

  const handleCancelClick = () => {
    if (!formik.isSubmitting) {
      resetState()
      props.onCancel?.()
    }
  }

  useEffect(() => {
    if (!props.keepCurrentAuthAlive) {
      const auth = getAuth()
      if (auth) {
        setTempAuth(auth)
        removeAuth()
      } else {
        props.onFailedDetectCurrentAuth?.()
      }
    }
  }, [props.keepCurrentAuthAlive])

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return (
    <>
      {props.show && (
        <>
          {!props.isModal && (
            <Col xs={12} className='text-center'>
              <div className='separator separator-content mt-4 mb-6'>
                <h2 className='text-gray-900 fs-1 text-nowrap'>Change Password</h2>
              </div>
              {props.subMessage && <span>{props.subMessage}</span>}
            </Col>
          )}
          <Col xs={12}>
            <Form>
              {props.isModal && (
                <>{props.subMessage && <Alertize variant='danger' faIcon={'fas fa-triangle-exclamation'} description={<>{props.subMessage}</>} />}</>
              )}
              {!success && (
                <>
                  {error ? (
                    <Alertize variant='danger' faIcon={'fas fa-triangle-exclamation'} description={<>{error}</>} />
                  ) : (
                    <>
                      {/* {props.showSpinner && (
                    <div className='d-flex flex-column'>
                      <div className='position-relative min-h-100px'>
                        <Loading show={loading} type='spinner' text='Updating...' />
                      </div>
                    </div>
                  )} */}
                    </>
                  )}
                </>
              )}
              <Form.Group>
                <Form.Label className={`fw-bold fs-6 required`}>Current Password</Form.Label>
                <Col className='d-flex flex-column gap-2'>
                  <Form.Control
                    {...formik.getFieldProps('currentPassword')}
                    className={`${GetFormikValidClass(formik, 'currentPassword')}`}
                    type='password'
                    placeholder='Current Password'
                    value={formik.values.currentPassword || ''}
                    disabled={formik.isSubmitting}
                    autoComplete='current-password'
                  />
                  <FormikErrors formik={formik} fieldKey={'currentPassword'} />
                </Col>
              </Form.Group>

              <Form.Group data-kt-password-meter='true'>
                <Form.Label className={`fw-bold fs-6 required`}>New Password</Form.Label>
                <Col className='d-flex flex-column gap-2'>
                  <Form.Control
                    {...formik.getFieldProps('newPassword')}
                    className={`${GetFormikValidClass(formik, 'newPassword')}`}
                    type='password'
                    placeholder='New Password'
                    value={formik.values.newPassword || ''}
                    disabled={formik.isSubmitting}
                    autoComplete='new-password'
                  />
                  <FormikErrors formik={formik} fieldKey={'newPassword'} />

                  <div className='d-flex align-items-center mt-3' data-kt-password-meter-control='highlight'>
                    <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                    <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                    <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                    <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
                  </div>

                  <div className='text-muted'>
                    Password must be at least {VALIDATION.PASSWORD.FORM.MIN} characters long with the combination of 
                    uppercase letters, lowercase letters, numbers, and symbols.
                  </div>
                </Col>
              </Form.Group>

              <Form.Group>
                <Form.Label className={`fw-bold fs-6 required`}>Confirm New Password</Form.Label>
                <Col className='d-flex flex-column gap-2'>
                  <Form.Control
                    {...formik.getFieldProps('newPasswordConfirmation')}
                    className={`${GetFormikValidClass(formik, 'newPasswordConfirmation')}`}
                    type='password'
                    placeholder='New Password Confirmation'
                    value={formik.values.newPasswordConfirmation || ''}
                    disabled={formik.isSubmitting}
                    autoComplete='new-password'
                  />
                  <FormikErrors formik={formik} fieldKey={'newPasswordConfirmation'} />
                </Col>
              </Form.Group>

              <Form.Group
                className={clsx({
                  'mt-10': !props.isModal,
                  'mt-7 mx-n7 p-7 pb-0 border border-left-0 border-right-0 border-bottom-0': props.isModal,
                })}
              >
                <div
                  className={clsx('d-flex flex-wrap gap-2', {
                    'justify-content-end': props.isModal,
                  })}
                >
                  <Button
                    type='submit'
                    className={clsx({
                      'flex-grow-1': !props.isModal,
                    })}
                    onClick={(e) => {
                      e.preventDefault()
                      formik.handleSubmit()
                    }}
                    disabled={formik.isSubmitting}
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        {props.isModal && <Iconize faIcon={`fas fa-check`} position='start' />}
                        Update
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                        Please wait...
                      </span>
                    )}
                  </Button>
                  {props.showCancel && (
                    <Button
                      variant='light'
                      className={clsx({
                        'flex-grow-1': !props.isModal,
                      })}
                      onClick={handleCancelClick}
                      disabled={formik.isSubmitting}
                    >
                      {props.isModal && <Iconize faIcon={`fas fa-xmark`} position='start' />}
                      Cancel
                    </Button>
                  )}
                </div>
              </Form.Group>
            </Form>
          </Col>
        </>
      )}
    </>
  )
}

ChangePassword.defaultProps = {
  isModal: false,
  showCancel: true,
  keepCurrentAuthAlive: true,
}

export default ChangePassword
